import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  PrimarySpinner,
  StatusUI,
  toast,
} from "@repo/ui";
import { formatLocalDateShortDay, NotFoundError } from "@repo/utils";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import type React from "react";
import { type ReactNode, Suspense, useEffect, useState } from "react";
import { RiFilePdfFill } from "react-icons/ri";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { PublishStatement } from "~/components/StatementActions/PublishStatement/PublishStatement";
import { RegenerateStatement } from "~/components/StatementActions/RegenerateStatement/RegenerateStatement";
import { VoidStatement } from "~/components/StatementActions/VoidStatement/VoidStatement";
import { graphql } from "~/gql/client";
import type { AdminStatementDetailPageQuery } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute("/__auth/statements_/$statementId")({
  head: () => opsHead("Statement"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const StatementDetailUI: React.FC<{
  statementDetail: NonNullable<
    AdminStatementDetailPageQuery["node"] & {
      __typename:
        | "ActivityStatement"
        | "BalanceStatement"
        | "MonthlyStatement"
        | "AccountStatement";
    }
  >;
}> = ({ statementDetail }) => {
  useOpsTitle(`Statement #${statementDetail.id.slice(-4).toUpperCase()}`);

  const actionGroups: ReactNode[] = [
    statementDetail.status === "STAFF_REVIEW_REQUIRED" && (
      <PublishStatement id={statementDetail.id} />
    ),
    (statementDetail.status === "STAFF_REVIEW_REQUIRED" ||
      statementDetail.status === "FAILED") && (
      <RegenerateStatement id={statementDetail.id} />
    ),
    (statementDetail.status === "PUBLISHED" ||
      statementDetail.status === "STAFF_REVIEW_REQUIRED" ||
      statementDetail.status === "FAILED") && (
      <VoidStatement id={statementDetail.id} />
    ),
  ].filter(Boolean);

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`Statement #${statementDetail.id.slice(-4).toUpperCase()}`}
        subtitle={<div>{statementDetail.id}</div>}
      />
      <div>
        <DetailRow
          label="Status"
          value={
            statementDetail.status && (
              <StatusUI {...{ status: statementDetail.status }} />
            )
          }
        />
        {statementDetail.generationFailureReason && (
          <DetailRow
            label="Failure reason"
            value={
              <pre className="whitespace-pre-wrap">
                {statementDetail.generationFailureReason}
              </pre>
            }
          />
        )}
        <DetailRow
          label="Organisation"
          value={
            statementDetail.organisation && (
              <Link
                to={`/organisations/${statementDetail.organisation.id}`}
                className="text-primary-interactive hover:text-primary-interactive"
              >
                {statementDetail.organisation.name}
              </Link>
            )
          }
        />
        <DetailRow
          label="Type"
          value={friendlyStatementType(statementDetail.__typename)}
        />
        <DetailRow
          label="Date"
          value={`${
            statementDetail.__typename === "ActivityStatement" ||
            statementDetail.__typename === "MonthlyStatement"
              ? `${formatLocalDateShortDay(
                  statementDetail.periodStartAtInclusive,
                )} - `
              : ""
          }${formatLocalDateShortDay(statementDetail.periodEndAtExclusive)}`}
        />
        <DetailRow
          label="File"
          value={
            statementDetail.downloadAvailable ? (
              statementDetail.documentName
            ) : (
              <div>
                The system hasn't generated a file for this statement yet.
              </div>
            )
          }
        />
        <DetailRow
          label="Generated content"
          value={
            statementDetail.generatedContent ? (
              <Dialog>
                <DialogTrigger asChild>
                  <div className="cursor-pointer hover:underline">
                    View input
                  </div>
                </DialogTrigger>
                <DialogContent className="flex h-[90dvh] flex-col sm:max-w-7xl">
                  <DialogHeader>
                    <DialogTitle>{` View raw input data `}</DialogTitle>
                  </DialogHeader>
                  <pre className="size-full overflow-auto">
                    {statementDetail.generatedContent}
                  </pre>
                </DialogContent>
              </Dialog>
            ) : (
              <div>
                The system hasn't generated content for this statement yet.
              </div>
            )
          }
        />

        {actionGroups.length > 0 && (
          <h2 className="mt-16 border-b font-semibold text-lg">Actions</h2>
        )}
        {actionGroups}

        {statementDetail.downloadAvailable && (
          <div className="mt-16">
            <h2 className="mb-2 border-b font-semibold text-lg">Review</h2>
            {statementDetail.documentType !== "PDF" ? (
              <div>
                No inline view possible for this statement.{" "}
                <StatementObjectDownload statementId={statementDetail.id} />
              </div>
            ) : (
              <StatementObjectInlineView
                className="h-dvh"
                {...{
                  statementId: statementDetail.id,
                }}
              />
            )}
          </div>
        )}
      </div>
    </AdminPageContainer>
  );
};

const StatementObjectInlineView: React.FC<{
  statementId: string;
  className?: string;
}> = ({ statementId, className }) => {
  const [getDownloadUrl, { data }] = useMutation(
    graphql(`
      mutation StatementInlineView($input: DownloadStatementInput!) {
        downloadStatement(input: $input) {
          documentName
          documentUrl
        }
      }
    `),
  );

  useEffect(() => {
    getDownloadUrl({
      variables: {
        input: {
          statementId,
        },
      },
    });
  }, [statementId, getDownloadUrl]);

  if (!data?.downloadStatement.documentUrl) {
    return (
      <div className={className}>
        <PrimarySpinner />
      </div>
    );
  }

  return (
    <div className={className}>
      <iframe
        title={statementId}
        loading="lazy"
        className="size-full"
        src={data.downloadStatement.documentUrl}
      />
    </div>
  );
};

type StatementObjectDownloadProps = {
  statementId: string;
  variant?: "button" | "link";
  hideIcon?: boolean;
  className?: string;
};

function StatementObjectDownload({
  statementId,
  variant = "button",
  hideIcon,
  className,
}: StatementObjectDownloadProps) {
  const [getDownloadUrl, { loading: mutationLoading }] = useMutation(
    graphql(`
      mutation ownloadStatement($input: DownloadStatementInput!) {
        downloadStatement(input: $input) {
          documentName
          documentUrl
        }
      }
    `),
  );

  const [loading, setLoading] = useState(false);

  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    setLoading(true);

    const defaultErrorTitle = "Something went wrong preparing your statement";
    const defaultErrorMessage = "Please try again later.";

    try {
      await getDownloadUrl({
        variables: { input: { statementId } },
        onCompleted: (data) => {
          const { documentUrl, documentName } = data.downloadStatement ?? {};
          if (!documentUrl || !documentName) {
            toast({
              kind: "error",
              title: defaultErrorTitle,
              description: defaultErrorMessage,
              autoHide: true,
            });
            return;
          }
          const anchor = document.createElement("a");
          anchor.href = documentUrl;
          anchor.target = "_blank";
          anchor.download = documentName ?? "file";
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        },
        onError: (error) => {
          const description =
            error.graphQLErrors?.[0]?.message ?? defaultErrorMessage;
          toast({
            kind: "error",
            title: "Something went wrong preparing your statement",
            description,
            autoHide: true,
          });
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const isDisabled = loading || mutationLoading;

  if (variant === "link") {
    return (
      <a
        href="#"
        onClick={handleDownload}
        className={cn(
          `text-sm hover:underline ${
            isDisabled ? "pointer-events-none opacity-50" : ""
          }`,
          className,
        )}
      >
        {!hideIcon && <RiFilePdfFill className="mr-1" />} Download
      </a>
    );
  }

  return (
    <Button
      variant="outline"
      leftIcon={!hideIcon ? <RiFilePdfFill /> : undefined}
      disabled={isDisabled}
      onClick={handleDownload}
      className={className}
    >
      Download
    </Button>
  );
}

function View() {
  const { statementId } = useParams({ strict: false });
  if (!statementId) {
    throw new NotFoundError();
  }

  const { data, loading } = useQuery(
    graphql(`
      query AdminStatementDetailPage($id: ID!) {
        node(id: $id) {
          id
          ... on Statement {
            id
            documentName
            documentType
            downloadAvailable
            generatedContent
            generationFailureReason
            organisation {
              id
              name
            }
            periodEndAtExclusive
            status
          }
          ... on ActivityStatement {
            periodStartAtInclusive
          }
          ... on MonthlyStatement {
            periodStartAtInclusive
          }
        }
      }
    `),
    {
      variables: {
        id: statementId,
      },
    },
  );

  if (loading) {
    return <PrimarySpinner />;
  }

  if (
    !data?.node ||
    (data.node.__typename !== "ActivityStatement" &&
      data.node.__typename !== "BalanceStatement" &&
      data.node.__typename !== "MonthlyStatement" &&
      data.node.__typename !== "AccountStatement")
  ) {
    throw new NotFoundError();
  }

  return <StatementDetailUI statementDetail={data.node} />;
}

function friendlyStatementType(
  statementTypename:
    | "ActivityStatement"
    | "BalanceStatement"
    | "MonthlyStatement"
    | "AccountStatement",
) {
  switch (statementTypename) {
    case "ActivityStatement":
      return "Activity";
    case "BalanceStatement":
      return "Balance";
    case "MonthlyStatement":
      return "Monthly";
    case "AccountStatement":
      return "Account";
  }
}
